/**
 * Permission codes the pricing-authority screens declare an interest in.
 *
 * These are the dot-strings `internal/pricing/permissions.go` registers with the
 * RBAC catalogue (design C2 §6.1). They live in the module rather than in
 * `src/lib/permissions.ts` because that file is shared by every screen and this
 * module is the only consumer — the split `@/modules/rules/permissions` and
 * `@/modules/tax/permissions` already make.
 *
 * Three things the server's split says, kept rather than collapsed:
 *
 *  - **`precedence.manage` ⊗ `precedence.activate` are a declared SoD pair.**
 *    Whoever writes the order that decides which price wins must not be the one
 *    who puts it in force. So the editor never offers Activate off the back of
 *    Submit: they are two buttons, gated on two codes, and the second is
 *    step-up-MFA'd server-side.
 *  - **`price.manage` ⊗ `price.override` are the second pair.** A person who can
 *    configure a price they intend to override anyway makes the override report
 *    show nothing unusual. `PriceResolveDrawer` therefore offers a manual price
 *    only to a holder of `price.override`, and never infers it from `manage`.
 *  - **`explanation.read` guards a waterfall, which names a customer, their
 *    contract price and their tier.** The server scopes every explanation read
 *    to the caller's permitted companies and takes a document reference rather
 *    than a bare log id (design §6.1, standing rule 9). The screens ask by
 *    document for the same reason; nothing here enumerates log ids.
 */

export const PRICING_PERMISSIONS = {
  /** Price lists, promotions, item settings, categories — the read half. */
  priceRead: 'pricing.price.read',
  priceManage: 'pricing.price.manage',
  /** Keying a manual unit price in place of the engine's answer. */
  priceOverride: 'pricing.price.override',

  precedenceRead: 'pricing.precedence.read',
  precedenceManage: 'pricing.precedence.manage',
  /** Step-up MFA server-side, and in SoD conflict with `precedenceManage`. */
  precedenceActivate: 'pricing.precedence.activate',

  priceSetRead: 'pricing.priceset.read',
  priceSetManage: 'pricing.priceset.manage',
  priceSetPublish: 'pricing.priceset.publish',

  /** The waterfall on a document line, and its CSV export. */
  explanationRead: 'pricing.explanation.read',

  /* ------------------------------------------------- other modules' codes */

  /** Every price list, promotion and price set is scoped to one company. */
  companyRead: 'org.company.read',
  /** A price is a price *of an item*; without this the screens show ids. */
  itemRead: 'inventory.item.read',
  /** A contract list and a resolve preview both name a customer. */
  customerRead: 'ar.customer.read',
  /** A price set may be scoped to one location (C8's outlet seam). */
  locationRead: 'org.location.read',
} as const

export type PricingPermission = (typeof PRICING_PERMISSIONS)[keyof typeof PRICING_PERMISSIONS]
